import * as v0 from "@aidkitorg/types/lib/survey";
import { DistroQuestionProps } from "./Props";
import React, { useState, useEffect, useCallback, useContext } from 'react';
import { usePost } from "../API";
import { useModularMarkdown } from "../Hooks/ModularMarkdown";
import { ArrowDownOnSquareIcon, DocumentIcon, ExclamationCircleIcon } from "@heroicons/react/24/outline";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import InterfaceContext from "../Context";
import { Button, Alert, Loading } from "@aidkitorg/component-library";
import { evalDistroConditional } from "@aidkitorg/roboscreener/lib/compute/util";

// TODO: Replace with actual i18n integration
const L = {
  questions: {
    document_generator: {
      document_generated: "Document generated",
      generating: "Generating...",
      retry: "Retry Generation",
      generate: "Generate {1}",
      download: "Download {1}",
      not_eligible: "Not yet eligible for {1}"
    }
  }
};

export default function DocumentGenerator({
  component,
  info,
  setInfo,
  uid,
}: DistroQuestionProps<v0.DocumentGenerator>) {
  const context = useContext(InterfaceContext);

  const generateDocument = usePost("/document/generate_from_template");
  const computeVariables = usePost("/document/compute_template_variables");
  const viewDocument = usePost("/v2/document/view");

  const [isGenerating, setIsGenerating] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isEligible, setIsEligible] = useState(false);

  const targetField = component.targetField;
  const statusField = `${targetField}_status`;
  const errorField = `${targetField}_error`;

  const currentDocumentUrl = info[targetField];
  const currentStatus = info[statusField];
  const currentError = info[errorField];

  // Evaluate eligibility condition
  useEffect(() => {
    (async () => {
      if (component.triggerCondition) {
        const result = await evalDistroConditional(component.triggerCondition, info, false);
        setIsEligible(!!result);
      } else {
        setIsEligible(true); // No condition = always eligible
      }
    })();
  }, [component.triggerCondition, info]);

  const handleGenerate = useCallback(async () => {
    if (isGenerating) return;

    setIsGenerating(true);
    setInfo(statusField, 'generating');
    setInfo(errorField, '');

    try {
      // Compute variables using backend API (handles Field, AsDate, etc.)
      // If uid exists, fetch from DB; otherwise pass info directly (distro context)
      const computeResponse = await computeVariables(
        uid
          ? { variables: component.variables, applicantUids: [uid] }
          : { variables: component.variables, info: { [uid]: info } }
      );

      if ('error' in computeResponse) {
        setInfo(statusField, 'error');
        setInfo(errorField, computeResponse.error);
        return;
      }

      const variables = computeResponse.results[uid || '_distro'] || {};

      const response = await generateDocument({
        templateUrl: component.templateUrl,
        variables,
        documentName: component.documentName,
        applicantUid: uid,
      });

      if ('error' in response) {
        setInfo(statusField, 'error');
        setInfo(errorField, response.error);
      } else if ('url' in response) {
        setInfo(targetField, response.url);
        setInfo(statusField, 'complete');
      }
    } catch (e: any) {
      setInfo(statusField, 'error');
      setInfo(errorField, e.message || 'Unknown error occurred');
    } finally {
      setIsGenerating(false);
    }
  }, [component, computeVariables, generateDocument, setInfo, targetField, statusField, errorField, isGenerating, uid, info]);

  const handleDownload = useCallback(async () => {
    if (!currentDocumentUrl || isDownloading) return;

    setIsDownloading(true);
    try {
      const { paths } = await viewDocument({ paths: [currentDocumentUrl] });
      if (paths && paths[currentDocumentUrl]?.[0]) {
        window.open(paths[currentDocumentUrl][0], '_blank');
      }
    } catch (e) {
      console.error("Error getting signed URL:", e);
    } finally {
      setIsDownloading(false);
    }
  }, [currentDocumentUrl, viewDocument, isDownloading]);

  const contentMarkdown = useModularMarkdown({
    content: component.content?.[context.lang] || component.content?.['en'] || '',
    info,
    uid
  });

  const hasDocument = currentDocumentUrl && currentStatus === 'complete';
  const hasError = currentStatus === 'error' && currentError;

  return (
    <div className="document-generator">
      {contentMarkdown && (
        <div className="mb-4">
          {contentMarkdown}
        </div>
      )}

      {!isEligible ?
        <em>{L.questions.document_generator.not_eligible.replace('{1}', component.documentName)}</em>
        : (
          <>
            {/* Show error if any */}
            {hasError && (
              <Alert variant="danger" className="mb-4">
                <div className="flex items-center gap-2">
                  <ExclamationCircleIcon className="h-5 w-5" />
                  <span>{currentError}</span>
                </div>
              </Alert>
            )}

            {/* Show success status if document exists */}
            {hasDocument && (
              <div className="flex items-center gap-2 text-green-600 mb-4">
                <CheckCircleIcon className="h-5 w-5" />
                <span>{L.questions.document_generator.document_generated}</span>
              </div>
            )}

            <div className="flex items-center gap-4">
              {/* Generate button - show if no document yet, or if there was an error */}
              {(!hasDocument || hasError) && (
                <Button
                  variant="primary"
                  onClick={handleGenerate}
                  loading={isGenerating}
                  disabled={isGenerating}
                >
                  {isGenerating ? (
                    <span>
                      {L.questions.document_generator.generating}
                    </span>
                  ) : hasError ? (
                    <span>
                      {L.questions.document_generator.retry}
                    </span>
                  ) : (
                    <div className="flex items-center">
                      <DocumentIcon className="h-5 w-5 mr-2" />
                      {L.questions.document_generator.generate.replace('{1}', component.documentName)}
                    </div>
                  )}
                </Button>
              )}

              {/* Download button - show if document exists */}
              {hasDocument && component.showDownloadButton !== false && (
                <Button
                  variant="secondary"
                  onClick={handleDownload}
                  loading={isDownloading}
                  disabled={isDownloading}
                >
                  <ArrowDownOnSquareIcon className="h-5 w-5 mr-2" />
                  <span>{L.questions.document_generator.download.replace('{1}', component.documentName)}</span>
                </Button>
              )}
            </div>
          </>
        )}
    </div>
  );
}
